import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import get from 'lodash/get';
import ArrayHelper from 'foundation/array-helper';
import Scrollable from 'general/js/scrollable';
import {
    getTotalBemClass,
    ScorePlayerCell,
    ScoreCell,
} from 'components/scores';
import {
    getCellClassList,
    setHoleAsActive,
    htmlScorecardCellElement,
    holeCellMouseOverHandler
} from './live-player-helpers';
import { livePlayerCellTypes } from './enums';
import { hasValue, isNullOrUndefined, isNumber } from '../../../general/js/general-helpers';
import { ROOT_CSS_CLASS } from './live-player-scores-constants';
import { formatClipTagParameters } from '../../shot-tracker/js/shot-tracker-helpers';
import { PLAYOFF_ROUND_ID } from '../../../general/js/constants';

class LivePlayerScores extends React.Component {
    constructor(props) {
        super(props);

        this.rootRef = React.createRef();
        this.innerRef = React.createRef();
        this.headLineRef = React.createRef();
        this.holeParLineRef = React.createRef();

        this.setClipTagParamsOnCellClick = this.setClipTagParamsOnCellClick.bind(this);

        this.scrollable = null;
        this.activeHoleData = { };
    }

    componentDidMount() {
        if (this.props.isScrollable) {
            this.scrollable = new Scrollable(
                this.rootRef.current,
                this.innerRef.current,
            );
        }
    }

    componentDidUpdate() {
        const { isClipTagLoaded } = this.props;
        if (isClipTagLoaded) {
            this.initActiveHole();
        }
    }

    initActiveHole() {
        const { clipTagParameters } = this.props;
        let holeId = null;
        let roundId = null;

        if (hasValue(clipTagParameters)) {
            const { hole, round } = clipTagParameters;

            if (!hasValue(this.activeHoleData)) {
                this.activeHoleData = clipTagParameters;
                holeId = hole;
                roundId = round;
            }

            if (this.props.selectedRoundId === this.activeHoleData.round) {
                holeId = hole;
                roundId = round;
            }
        }

        setHoleAsActive(holeId, roundId, { headLine: [this.headLineRef.current], holeParLine: [this.holeParLineRef.current] });
    }

    componentWillUnmount() {
        if (this.scrollable) {
            this.scrollable.destroy();
        }
    }

    setClipTagParamsOnCellClick(holeId) {
        const { setClipTagParameters, clipTagParameters, selectedRoundId } = this.props;
        const { teamNo, eventCentreParams, playOffData } = clipTagParameters;


        const newParams = formatClipTagParameters(teamNo, selectedRoundId, holeId, eventCentreParams, playOffData);

        if (hasValue(newParams)) {
            if (setClipTagParameters(newParams)) {
                this.activeHoleData = newParams;
            }
        }
    }

    getR2HCellAttributes(holeIdData, matchingCellsContainer) {
        const { isClipTagLoaded } = this.props;

        if (!isClipTagLoaded ||
            !hasValue(holeIdData) ||
            isNullOrUndefined(holeIdData.holeId) ||
            isNullOrUndefined(holeIdData.holeRound) ||
            !hasValue(matchingCellsContainer)) {
            return {};
        }

        return {
            onClick: () => this.setClipTagParamsOnCellClick(holeIdData.holeId),
            onMouseEnter: () => holeCellMouseOverHandler(holeIdData.holeId, matchingCellsContainer, true),
            onMouseLeave: () => holeCellMouseOverHandler(holeIdData.holeId, matchingCellsContainer, false),
            'cell-hole-round': [holeIdData.holeRound],
            'cell-hole-id': [holeIdData.holeId]
        };
    }

    renderCell(key, value = '', type = {}, holeId = undefined) {
        const { scoringPlayerRound, isClipTagLoaded } = this.props;
        const { id } = scoringPlayerRound;

        let matchingCellsContainer = { holeParLine: [this.holeParLineRef.current], headLine: [this.headLineRef.current] };

        if (holeId === null) {
            holeId = value;
            matchingCellsContainer = { headLine: [this.headLineRef.current], holeParLine: [this.holeParLineRef.current] };
        }

        const applyR2HLinkStyling = isClipTagLoaded && isNumber(holeId);

        return htmlScorecardCellElement(value,
            key,
            getCellClassList(ROOT_CSS_CLASS, type, applyR2HLinkStyling, livePlayerCellTypes.HOLE_CELL),
            this.getR2HCellAttributes({ holeId, holeRound: id }, matchingCellsContainer));
    }

    renderPlayerPar(key, holePar, value, holeIdData = {}) {
        const r2hScoreCellAttributes = this.getR2HCellAttributes(holeIdData, { headLine: [this.headLineRef.current], holeParLine: [this.holeParLineRef.current] });
        const { isClipTagLoaded } = this.props;

        return (
            <ScorePlayerCell
                key={key}
                childCss={getCellClassList(ROOT_CSS_CLASS, {}, isClipTagLoaded, livePlayerCellTypes.PLAYER_CELL)}
                r2hAttributes={r2hScoreCellAttributes}
                holePar={holePar}
                playerPar={value}
            />
        );
    }

    renderPlayerTotalPar(holePar, value, type = {}) {
        const typeCss = classNames({
            'is-wide': type.isWide
        });

        const difference = value - holePar;
        let bemList = ['default', 'has-border'];

        if (value && holePar && !Number.isNaN(difference)) {
            bemList = [getTotalBemClass(difference), 'has-border'];
        }

        return (
            <ScoreCell
                childCss={`${ROOT_CSS_CLASS}__player-cell ${typeCss}`}
                bemList={bemList}
            >{value || '-'}</ScoreCell>
        );
    }

    isPlayOff() {
        const { selectedRoundId } = this.props;

        return selectedRoundId === PLAYOFF_ROUND_ID;
    }

    renderHeadLine() {
        const { scoringCourseRound, isFull } = this.props;

        return (
            <div className={`${ROOT_CSS_CLASS}__head`} ref={this.headLineRef}>
                {
                    scoringCourseRound.holePars.slice(0, 9).map((holePar, i) =>
                        this.renderCell(`h-${i}`, holePar.holeId, {}, null))
                }
                {isFull && !this.isPlayOff() && this.renderCell('h-in', 'Out', { isWide: true })}
                {
                    scoringCourseRound.holePars.slice(9, 18).map((holePar, i) =>
                        this.renderCell(`h-${i}`, holePar.holeId, {}, null))
                }
                {isFull && !this.isPlayOff() && this.renderCell('h-out', 'In', { isWide: true })}
                {isFull && !this.isPlayOff() && this.renderCell('h-tot', 'Tot', { isWide: true })}
            </div>
        );
    }

    renderHoleParLine() {
        const { scoringCourseRound, isFull } = this.props;

        return (
            <div className={`${ROOT_CSS_CLASS}__row`} ref={this.holeParLineRef}>
                {
                    scoringCourseRound.holePars.slice(0, 9).map((holePar, i) =>
                        this.renderCell(`r-${i}`, holePar.par, {}, holePar.holeId))
                }
                {isFull && !this.isPlayOff() && this.renderCell('r-out', scoringCourseRound.outPar, { isWide: true })}
                {
                    scoringCourseRound.holePars.slice(9, 18).map((holePar, i) =>
                        this.renderCell(`r-${i}`, holePar.par, {}, holePar.holeId))
                }
                {isFull && !this.isPlayOff() && this.renderCell('r-in', scoringCourseRound.inPar, { isWide: true })}
                {isFull && !this.isPlayOff() && this.renderCell('r-tot', scoringCourseRound.totalPar, { isWide: true })}
            </div>
        );
    }

    renderPlayerParLine() {
        const { isFull } = this.props;
        const { scoringCourseRound, scoringPlayerRound } = this.props;

        if (!scoringPlayerRound ||
            ArrayHelper.isEmpty(scoringPlayerRound.info)
        ) {
            return null;
        }

        return (
            <div className={`${ROOT_CSS_CLASS}__player`}>
                {
                    scoringCourseRound.holePars.slice(0, 9).map((holePar, i) => {
                        const playerPar = get(scoringPlayerRound, `info[${i}].playerPar`) || null;
                        const holeIdData = { holeId: parseInt(i + 1), holeRound: scoringPlayerRound.id };

                        return this.renderPlayerPar(`p-${i}`, holePar.par, playerPar, holeIdData);
                    })
                }
                {
                    isFull && !this.isPlayOff() &&
                    this.renderPlayerTotalPar(
                        scoringCourseRound.outPar,
                        scoringPlayerRound.outPar,
                        { isWide: true }
                    )
                }
                {
                    scoringCourseRound.holePars.slice(9, 18).map((holePar, i) => {
                        const playerPar = get(scoringPlayerRound, `info[${i + 9}].playerPar`) || null;
                        const holeIdData = { holeId: parseInt(i + 10), holeRound: scoringPlayerRound.id };

                        return this.renderPlayerPar(`p-${i}`, holePar.par, playerPar, holeIdData);
                    })
                }
                {
                    isFull && !this.isPlayOff() &&
                    this.renderPlayerTotalPar(
                        scoringCourseRound.inPar,
                        scoringPlayerRound.inPar,
                        { isWide: true }
                    )
                }
                {
                    isFull && !this.isPlayOff() &&
                    this.renderPlayerTotalPar(
                        scoringCourseRound.totalPar,
                        scoringPlayerRound.totalPar,
                        { isWide: true }
                    )
                }
            </div>
        );
    }

    render() {
        const { scoringPlayerRound, isScrollable, isWithdrawn } = this.props;
        const stateCss = classNames({
            'is-empty': !scoringPlayerRound || ArrayHelper.isEmpty(scoringPlayerRound.info),
            'is-scrollable': isScrollable,
            'is-withdrawn': isWithdrawn
        });

        return (
            <div className={`${ROOT_CSS_CLASS} ${stateCss}`} ref={this.rootRef}>
                <div className={`${ROOT_CSS_CLASS}__inner`} ref={this.innerRef}>
                    <div className={`${ROOT_CSS_CLASS}__list`}>
                        {this.renderHeadLine()}
                        {this.renderHoleParLine()}
                        {this.renderPlayerParLine()}
                    </div>
                </div>
            </div>
        );
    }
}

LivePlayerScores.propTypes = {
    scoringPlayerRound: PropTypes.object,
    scoringCourse: PropTypes.object,
    isFull: PropTypes.bool,
    isScrollable: PropTypes.bool,
    isClipTagLoaded: PropTypes.bool,
    clipTagParameters: PropTypes.object,
    setClipTagParameters: PropTypes.func,
    selectedRoundId: PropTypes.number
};

export default LivePlayerScores;
